import { useCallback, useContext } from 'react';
import ErrorBoundary from 'conversations-error-reporting/error-boundaries/components/ErrorBoundary';
import { ChannelLabel } from '../../../capability-editor/public/components';
import { ChannelCapabilitiesConfigContext } from 'conversations-thread-view/channel-capabilities/context/ChannelCapabilitiesConfigContext';
import { useFloatingEditorState } from '../hooks/useFloatingEditorState';
import { usePopOutEditorState } from '../../../pop-out/public/hooks';
import { useShepherdDismissState } from '../hooks/useShepherdDismissState';
import styled from 'foundations-theming/utils';
import { DialogButton } from 'foundations-components/transitional/components/DialogButton';
import { EnlargeIcon, ExitFullScreenIcon, RemoveIcon } from 'foundations-assets';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
// import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ATTRIBUTE_STORAGE_KEY = `floating-composer-fullscreen-shepherd`;
const Header = styled.header.withConfig({
  displayName: "FloatingComposeEditorWrapper__Header",
  componentId: "sc-1k51vg1-0"
})(["display:flex;flex-direction:row;align-items:center;justify-content:space-between;border-radius:inherit;padding-inline:", ";min-height:0;height:50px;background-color:", ";"], ({
  theme
}) => theme.space[400], ({
  theme
}) => theme.color['fill-accent-neutral-hover']);
const Wrapper = styled.div.withConfig({
  displayName: "FloatingComposeEditorWrapper__Wrapper",
  componentId: "sc-1k51vg1-1"
})(["display:flex;flex-direction:column;flex-grow:1;background:", ";box-shadow:rgb(0 0 0 / 35%) 0 1px 24px 0;position:relative;border-radius:", ";width:100%;"], ({
  theme
}) => theme.color['fill-surface-default'], ({
  theme
}) => `${theme.borderRadius[100]} ${theme.borderRadius[100]} ${theme.borderRadius[0]} ${theme.borderRadius[0]}`);
const GatedChannelLabel = () => {
  const {
    genericChannelId
  } = useContext(ChannelCapabilitiesConfigContext);
  return /*#__PURE__*/_jsx(Text, {
    as: "h5",
    color: "text-core-on-fill-default",
    children: /*#__PURE__*/_jsx(ChannelLabel, {
      genericChannelId: genericChannelId
    })
  });
};
GatedChannelLabel.displayName = 'GatedChannelLabel';
const renderIconHolder = props => {
  return /*#__PURE__*/_jsx(IconHolder, Object.assign({}, props, {
    color: "icon-core-on-fill-default"
  }));
};
export const FloatingComposeEditorWrapper = ({
  children
}) => {
  const {
    close
  } = useFloatingEditorState();
  const {
    isOpen: isExpanded,
    open: expand,
    close: collapse
  } = usePopOutEditorState('floating');
  const {
    isDismissed,
    setDismissed
  } = useShepherdDismissState({
    attributeKey: ATTRIBUTE_STORAGE_KEY
  });
  const expandAndDismissShepherd = useCallback(() => {
    expand();
    if (!isDismissed) setDismissed();
  }, [expand, setDismissed, isDismissed]);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsxs(Wrapper, {
      "data-selenium-test": "popout-composer-editor",
      children: [/*#__PURE__*/_jsxs(Header, {
        children: [/*#__PURE__*/_jsx(GatedChannelLabel, {}), /*#__PURE__*/_jsxs(Flex, {
          children: [/*#__PURE__*/_jsx(DialogButton, {
            "aria-label": "expand",
            icon: isExpanded ? ExitFullScreenIcon : EnlargeIcon,
            IconHolder: renderIconHolder,
            detached: false,
            marginInline: "200",
            onClick: isExpanded ? collapse : expandAndDismissShepherd,
            "data-selenium-test": "expand-popout-composer",
            "data-test-id": "floating-composer-expand-button"
          }), /*#__PURE__*/_jsx(DialogButton, {
            "aria-label": "close",
            marginInline: "200",
            onClick: close,
            icon: RemoveIcon,
            IconHolder: renderIconHolder,
            detached: false,
            "data-selenium-test": "close-popout-composer"
          })]
        })]
      }), /*#__PURE__*/_jsx(ErrorBoundary, {
        errorBoundaryName: "FloatingComposeEditor",
        errorLoggingTitle: "Inbox Floating Compose Editor",
        children: children
      }, "composer-ui")]
    })
  );
};
FloatingComposeEditorWrapper.displayName = 'FloatingComposeEditorWrapper';